import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { generatePath, useNavigate } from 'react-router-dom';
import { Endpoint } from '@/Config/api/endpoint';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { pushGtmEvent } from '@/Config/libs/analytics';
import { KnowledgeBaseApi } from '@/Domain/api/KnowledgeBase/KnowledgeBase.api';
import { ArticleDto, ArticleInputDto } from '@/Domain/api/KnowledgeBase/dto/Article.dto';
import { ErrorType } from '@/Domain/components/KnowledgeBaseArticles/types';
import { GtmEventEnum } from '@/Domain/enum/GtmEvent.enum';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

export const useCreateArticle = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { capture } = usePostHogAnalytics();

  const mutation = useMutation<ArticleDto, AxiosError<ErrorType<ArticleInputDto>>, ArticleInputDto>(
    {
      mutationKey: [Endpoint.knowledgeBase.articles.create],
      mutationFn: KnowledgeBaseApi.createArticle,
      onSuccess: (article) => {
        capture(PostHogEventEnum.ArticleCreated, {
          article_id: article.id,
          article_type: article.type,
        });
        pushGtmEvent(GtmEventEnum.ArticleCreated, {
          article_id: article.id,
          article_type: article.type,
        });

        queryClient.setQueryData<ArticleDto[]>(
          [QueryKey.knowledgeBase.articles.listOrFilter, '', []],
          (oldData = []) => [article, ...oldData],
        );

        navigate(generatePath(IndexPath.knowledgeBase.create, { articleId: article.id }), {
          viewTransition: true,
        });

        void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
        void queryClient.invalidateQueries({ queryKey: [QueryKey.onboarding.progress] });
      },
      onError: (error) => {
        console.error('Error creating article:', error);
      },
    },
  );

  const handleCreateArticle = (data: ArticleInputDto) => {
    mutation.mutate(data);
  };

  return {
    handleCreateArticle,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};
